import { Link } from "react-router-dom";
import { PlusCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChoiceCombobox, ChoiceSelect, ChoiceMultiSelect, FormField } from "@/components/forms";
import {
  applyCerToFirForm,
  cerEntriesToChoices,
  findCerEntry,
  useCerAttiviList,
} from "@/lib/cerAttiviChoices";
import type { FirBozzaFormState } from "@/lib/firBozzaForm";
import {
  contactsToChoices,
  useOperatoriByRole,
} from "@/lib/operatoriChoices";
import {
  PROVENIENZA,
  STATO_FISICO,
  UNITA_MISURA_FORM,
  useRentriChoices,
} from "@/lib/rentriChoices";

export function FirBozzaFormPanel({
  form,
  onChange,
}: {
  form: FirBozzaFormState;
  onChange: (next: FirBozzaFormState) => void;
}) {
  const cerQ = useCerAttiviList();
  const cerItems = cerQ.data?.items ?? [];
  const prodQ = useOperatoriByRole("produttore");
  const destQ = useOperatoriByRole("destinatario");
  const trasQ = useOperatoriByRole("trasportatore");
  const hpQ = useRentriChoices("caratteristiche-pericolo");

  const set = <K extends keyof FirBozzaFormState>(k: K, v: FirBozzaFormState[K]) =>
    onChange({ ...form, [k]: v });

  return (
    <div className="space-y-4">
      <FormField label="Unità locale (num_iscr_sito)" required>
        <Input
          className="font-mono text-sm"
          value={form.num_iscr_sito}
          onChange={(e) => set("num_iscr_sito", e.target.value)}
        />
      </FormField>
      <FormField label="Codice blocco vidimazione (opz.)">
        <Input
          className="font-mono text-sm"
          value={form.codice_blocco}
          onChange={(e) => set("codice_blocco", e.target.value)}
          placeholder="Es. NXTWB"
        />
      </FormField>

      {cerItems.length === 0 && !cerQ.isLoading && (
        <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm space-y-2">
          <p>Aggiungi codici in CER attivi prima di compilare il FIR.</p>
          <Button variant="outline" size="sm" asChild>
            <Link to="/cer-attivi">
              <PlusCircle className="mr-1.5 h-4 w-4" /> CER attivi
            </Link>
          </Button>
        </div>
      )}

      <FormField label="Codice EER (da CER attivi)" required>
        <ChoiceCombobox
          value={form.eer}
          onChange={(v) => {
            const entry = findCerEntry(cerItems, v);
            onChange(
              entry ? applyCerToFirForm(form, entry) : { ...form, eer: v },
            );
          }}
          options={cerEntriesToChoices(cerItems)}
          loading={cerQ.isLoading}
          formatValue={(v) => {
            const clean = v.replace(/\./g, "");
            return clean.length === 6
              ? `${clean.slice(0, 2)}.${clean.slice(2, 4)}.${clean.slice(4)}`
              : clean;
          }}
        />
      </FormField>

      <div className="grid grid-cols-2 gap-3">
        <FormField label="Quantità" required>
          <Input type="number" value={form.quantita} onChange={(e) => set("quantita", e.target.value)} />
        </FormField>
        <FormField label="Unità di misura" required>
          <ChoiceSelect
            value={form.unita_misura}
            onChange={(v) => set("unita_misura", v || "KG")}
            options={UNITA_MISURA_FORM}
            required
          />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <FormField label="Stato fisico" required>
          <ChoiceSelect
            value={form.stato_fisico}
            onChange={(v) => set("stato_fisico", v)}
            options={STATO_FISICO}
            required
          />
        </FormField>
        <FormField label="Provenienza" required>
          <ChoiceSelect
            value={form.provenienza}
            onChange={(v) => set("provenienza", v)}
            options={PROVENIENZA}
            required
          />
        </FormField>
      </div>

      <FormField label="Caratteristiche di pericolo (HP)">
        <ChoiceMultiSelect
          values={form.caratteristiche_pericolo}
          onChange={(vs) => set("caratteristiche_pericolo", vs)}
          options={hpQ.data ?? []}
          loading={hpQ.isLoading}
        />
      </FormField>

      <FormField label="Tipo trasporto">
        <ChoiceSelect
          value={form.tipo_trasporto}
          onChange={(v) => set("tipo_trasporto", v || "Terrestre")}
          options={[
            { value: "Terrestre", label: "Terrestre" },
            { value: "Marittimo", label: "Marittimo" },
            { value: "Ferroviario", label: "Ferroviario" },
          ]}
          required
        />
      </FormField>

      <p className="text-xs text-muted-foreground">
        Soggetti dalla rubrica{" "}
        <Link to="/contacts" className="underline font-medium">
          Operatori
        </Link>
        .
      </p>
      <FormField label="Produttore">
        <ChoiceCombobox
          value={form.produttore_cf}
          onChange={(v) => set("produttore_cf", v)}
          options={contactsToChoices(Array.isArray(prodQ.data) ? prodQ.data : [])}
          loading={prodQ.isLoading}
        />
      </FormField>
      <FormField label="Destinatario">
        <ChoiceCombobox
          value={form.destinatario_cf}
          onChange={(v) => set("destinatario_cf", v)}
          options={contactsToChoices(Array.isArray(destQ.data) ? destQ.data : [])}
          loading={destQ.isLoading}
        />
      </FormField>
      <FormField label="Trasportatore">
        <ChoiceCombobox
          value={form.trasportatore_cf}
          onChange={(v) => set("trasportatore_cf", v)}
          options={contactsToChoices(Array.isArray(trasQ.data) ? trasQ.data : [])}
          loading={trasQ.isLoading}
        />
      </FormField>
    </div>
  );
}
